// The guided first run and what it sets: brief times and quiet hours (/api/settings/briefs), the
// setup markers (/api/settings/setup), VIP suggestions (/api/senders/vip-suggestions) and the
// writing voice (/api/voice). Query keys and hooks live here too, beside their endpoints.
import { queryOptions, useQuery } from "@tanstack/react-query";
import type {
  BriefSchedule,
  BriefScheduleInput,
  SetupState,
  VipSuggestion,
  VoiceAnalysisJob,
  VoiceOverview,
} from "@shared/setup";
import { api } from "./api";

export const setupKeys = {
  setup: ["settings", "setup"] as const,
  briefs: ["settings", "briefs"] as const,
  vipSuggestions: ["senders", "vipSuggestions"] as const,
  voice: ["voice"] as const,
};

export const setupApi = {
  setup: (signal?: AbortSignal) => api<SetupState>("/api/settings/setup", { signal }),
  complete: () => api<SetupState>("/api/settings/setup/complete", { method: "POST" }),
  dismissTips: () => api<SetupState>("/api/settings/setup/tips-dismissed", { method: "POST" }),
  briefs: (signal?: AbortSignal) => api<BriefSchedule>("/api/settings/briefs", { signal }),
  saveBriefs: (body: BriefScheduleInput) =>
    api<BriefSchedule>("/api/settings/briefs", { method: "PUT", body }),
  vipSuggestions: (signal?: AbortSignal) =>
    api<VipSuggestion[]>("/api/senders/vip-suggestions?limit=10", { signal }),
  voice: (signal?: AbortSignal) => api<VoiceOverview>("/api/voice", { signal }),
  analyzeVoice: (accountId: string) =>
    api<VoiceAnalysisJob>("/api/voice/analyze", { method: "POST", body: { accountId } }),
};

export const setupQuery = queryOptions({
  queryKey: setupKeys.setup,
  queryFn: ({ signal }) => setupApi.setup(signal),
  staleTime: 60_000,
});

export function useSetupState() {
  return useQuery(setupQuery);
}

export function useBriefSchedule() {
  return useQuery({
    queryKey: setupKeys.briefs,
    queryFn: ({ signal }) => setupApi.briefs(signal),
    staleTime: 5 * 60_000,
  });
}

export function useVipSuggestions() {
  return useQuery({
    queryKey: setupKeys.vipSuggestions,
    queryFn: ({ signal }) => setupApi.vipSuggestions(signal),
    staleTime: 60_000,
  });
}

/** The writing voice; polls every few seconds while an analysis is running. */
export function useVoiceOverview() {
  return useQuery({
    queryKey: setupKeys.voice,
    queryFn: ({ signal }) => setupApi.voice(signal),
    refetchInterval: (query) =>
      query.state.data?.accounts.some((a) => a.analysis?.state === "running") ? 3000 : false,
  });
}
